import { isLiabilityAccount } from "@common/helpers/financialAccount";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import {
    FinancialAccount,
    FinancialAccountType,
} from "@common/types/financialAccount";

export type AccountClassMismatchDirection =
    | "liabilityToAsset"
    | "assetToLiability";

export interface AccountClassMismatch {
    importedAccountId: string;
    importedAccountName: string;
    importedType: FinancialAccountType;
    targetAccountName: string | null;
    targetType: FinancialAccountType;
    direction: AccountClassMismatchDirection;
}

type MappableImportedAccount = Pick<
    ImportedAccount,
    "id" | "name" | "financialType" | "mappedFinancialAccountId" | "isClosed"
>;

type MappingTargetAccount = Pick<
    FinancialAccount,
    "id" | "name" | "financialType"
>;

interface GetAccountClassMismatchesParams {
    importedAccounts: MappableImportedAccount[];
    financialAccounts: MappingTargetAccount[];
}

export function getAccountClassMismatches({
    importedAccounts,
    financialAccounts,
}: GetAccountClassMismatchesParams): AccountClassMismatch[] {
    const financialAccountById = new Map(
        financialAccounts.map((financialAccount) => [
            financialAccount.id,
            financialAccount,
        ]),
    );

    return importedAccounts.flatMap((importedAccount) => {
        if (
            importedAccount.isClosed ||
            importedAccount.mappedFinancialAccountId == null ||
            importedAccount.financialType == null
        ) {
            return [];
        }

        const target = financialAccountById.get(
            importedAccount.mappedFinancialAccountId,
        );
        if (!target || target.financialType == null) {
            return [];
        }

        const isImportedLiability = isLiabilityAccount(
            importedAccount.financialType,
        );
        if (isImportedLiability === isLiabilityAccount(target.financialType)) {
            return [];
        }

        return [
            {
                importedAccountId: importedAccount.id,
                importedAccountName: importedAccount.name,
                importedType: importedAccount.financialType,
                targetAccountName: target.name,
                targetType: target.financialType,
                direction: isImportedLiability
                    ? "liabilityToAsset"
                    : "assetToLiability",
            },
        ];
    });
}
